import { useCart, WHATSAPP_NUMBER } from "@/hooks/useCart";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

export default function CartDrawer() {
  const { items, isOpen, closeCart, removeItem, updateQuantity, totalPrice, totalItems, getWhatsAppMessage, clearCart } = useCart();

  const whatsappLink = items.length > 0
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${getWhatsAppMessage()}`
    : "#";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeCart}
            className="fixed inset-0 z-[60] bg-foreground-950/50 backdrop-blur-sm"
          />

          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="fixed top-0 right-0 z-[70] h-full w-full sm:w-[420px] bg-background-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
              <div className="flex items-center gap-2.5">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-shopping-bag-3-line text-xl"></i>
                </span>
                <div>
                  <h2 className="text-base font-heading font-semibold text-foreground-950">
                    Seu Carrinho
                  </h2>
                  <p className="text-xs text-foreground-500">
                    {totalItems} {totalItems === 1 ? "item" : "itens"}
                  </p>
                </div>
              </div>
              <button
                onClick={closeCart}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer text-foreground-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <span className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
                    <i className="ri-shopping-bag-3-line text-4xl"></i>
                  </span>
                  <h3 className="text-lg font-heading font-semibold text-foreground-800 mb-1">
                    Carrinho vazio
                  </h3>
                  <p className="text-sm text-foreground-500 mb-5">
                    Adicione produtos do cardápio para montar seu pedido.
                  </p>
                  <Link
                    to="/cardapio"
                    onClick={closeCart}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-restaurant-line"></i>
                    Ver Cardápio
                  </Link>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {items.map((item) => (
                    <motion.div
                      key={item.product.id}
                      layout
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      className="flex gap-3 p-3 rounded-xl bg-background-100 border border-background-200/60"
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-background-200">
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <h4 className="text-sm font-semibold text-foreground-950 truncate">
                              {item.product.name}
                            </h4>
                            <p className="text-xs text-foreground-500 mt-0.5">
                              {item.product.category === "mini-pasteis" ? "Cx 100 un" : item.product.weight}
                            </p>
                          </div>
                          <button
                            onClick={() => removeItem(item.product.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-200 transition-colors cursor-pointer shrink-0 text-foreground-400 hover:text-red-500"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>

                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                              className="w-7 h-7 flex items-center justify-center rounded-md border border-background-300 hover:bg-background-200 transition-colors cursor-pointer text-foreground-700"
                            >
                              <i className="ri-subtract-line text-sm"></i>
                            </button>
                            <span className="w-8 text-center text-sm font-semibold text-foreground-950">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                              className="w-7 h-7 flex items-center justify-center rounded-md border border-background-300 hover:bg-background-200 transition-colors cursor-pointer text-foreground-700"
                            >
                              <i className="ri-add-line text-sm"></i>
                            </button>
                          </div>
                          <span className="text-sm font-heading font-semibold text-primary-600">
                            R$ {(item.product.price * item.quantity).toFixed(2).replace(".", ",")}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-background-200/70 px-5 py-4 bg-background-50">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-foreground-600">Total</span>
                  <span className="text-xl font-heading font-bold text-primary-600">
                    R$ {totalPrice.toFixed(2).replace(".", ",")}
                  </span>
                </div>

                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeCart}
                  className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-xl bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  Enviar pedido pelo WhatsApp
                </a>

                <button
                  onClick={closeCart}
                  className="flex items-center justify-center gap-2 w-full mt-2.5 py-3 rounded-xl bg-transparent text-foreground-600 text-sm font-medium hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Continuar comprando
                </button>

                <button
                  onClick={clearCart}
                  className="flex items-center justify-center gap-1.5 w-full mt-3 py-2.5 rounded-xl bg-transparent text-foreground-400 text-xs font-medium hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-6-line text-sm"></i>
                  Limpar carrinho
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}